import { useId } from 'react';
import { Check } from 'lucide-react';
import { choiceBoxGridClass, type ChoiceBoxSplit } from './sideBySideChoices';

const GRID_CLASS = 'grid gap-3 sm:gap-4';
const BOX_CLASS =
  'flex flex-col justify-start text-left rounded-xl border-2 p-3 sm:p-4 h-full transition-all duration-200';
const BOX_SELECTED_CLASS = 'border-blue-500 bg-blue-50';
const BOX_UNSELECTED_CLASS = 'border-gray-300 bg-white hover:border-gray-400';
const BOX_HEADER_CLASS = 'flex items-start justify-between gap-2';
const BOX_NAME_CLASS = 'text-lg font-semibold text-blue-700';
const BOX_LINES_CLASS = 'mt-2 list-disc pl-4 sm:pl-5 space-y-1 text-sm text-gray-800 hyphens-auto';
const CHECK_BADGE_CLASS =
  'w-5 h-5 shrink-0 bg-blue-500 rounded-full flex items-center justify-center';
const CHECK_ICON_CLASS = 'w-3 h-3 text-white';

interface SideBySideChoiceBoxesProps {
  /** In the order the respondent is shown them. */
  choices: string[];
  boxes: ChoiceBoxSplit['boxes'];
  selected: unknown;
  disabled: boolean;
  onChoose: (choice: string) => void;
}

/** A single_choice question's options as boxes side by side (spec 111-01 §1). Text only. */
export function SideBySideChoiceBoxes({
  choices,
  boxes,
  selected,
  disabled,
  onChoose,
}: SideBySideChoiceBoxesProps) {
  const idPrefix = useId();
  return (
    <div className={`${GRID_CLASS} ${choiceBoxGridClass(choices.length)}`}>
      {choices.map((choice, index) => {
        const isSelected = selected === choice;
        const nameId = `${idPrefix}-${index}-name`;
        const linesId = `${idPrefix}-${index}-lines`;
        const lines = boxes[choice] ?? [];
        return (
          <button
            key={choice}
            type="button"
            disabled={disabled}
            onClick={() => onChoose(choice)}
            aria-labelledby={nameId}
            aria-describedby={lines.length > 0 ? linesId : undefined}
            className={`${BOX_CLASS} ${isSelected ? BOX_SELECTED_CLASS : BOX_UNSELECTED_CLASS}`}
          >
            <span className={BOX_HEADER_CLASS}>
              <span id={nameId} className={BOX_NAME_CLASS}>
                {choice}
              </span>
              {isSelected && (
                <span className={CHECK_BADGE_CLASS}>
                  <Check className={CHECK_ICON_CLASS} />
                </span>
              )}
            </span>
            {lines.length > 0 && (
              <ul id={linesId} className={BOX_LINES_CLASS}>
                {lines.map((line, lineIndex) => (
                  <li key={lineIndex}>{line}</li>
                ))}
              </ul>
            )}
          </button>
        );
      })}
    </div>
  );
}
